/**
 * components/landing/design-system/index.jsx — Phase 1 primitives.
 *
 * A tiny library of tokenised building blocks for the Kalki Ai Cloud
 * marketing site.  Everything here is intentionally light-touch and
 * dependency-free — just Tailwind + a couple of lucide-react icons —
 * so future phases can compose them without dragging in extra libs.
 *
 * Exports:
 *   Container            page-width wrapper (max-w-7xl, 2xl:max-w-[1400px])
 *   Eyebrow              small uppercase kicker above a heading
 *   SectionHeading       consistent H2 + optional H3 subtitle
 *   SectionDivider       subtle gradient rule
 *   GradientButton       primary CTA — orange gradient with soft glow
 *   GhostButton          secondary CTA — bordered glass
 *   GlassCard            translucent card with border + backdrop blur
 *   FeatureCard          icon + title + body, tuned for feature grids
 *   StatCard             large numeric + label + optional trend chip
 *   Marquee              horizontal auto-scroll wrapper (partner logos)
 *   PageShell            page-level wrapper providing hero + slot
 *   FadeInUp / Stagger   simple motion helpers built on CSS transitions
 *
 * All colour tokens map to the palette declared in tailwind.config.js
 * (brand-gold, brand-emerald, brand-black, brand-ink, brand-surface, …).
 */

import React from 'react'
import clsx from 'clsx'
import Link from '@/shims/NextLink'
import { ArrowRight, ArrowUpRight, TrendingUp } from 'lucide-react'

// ─── Container ────────────────────────────────────────────────────
export const Container = ({ className = '', children, size = 'default', ...rest }) => (
  <div
    {...rest}
    className={clsx(
      'mx-auto w-full px-4 sm:px-6 lg:px-8',
      size === 'wide'    && 'max-w-[1440px]',
      size === 'narrow'  && 'max-w-4xl',
      size === 'default' && 'max-w-7xl',
      className,
    )}
  >
    {children}
  </div>
)

// ─── Eyebrow (small uppercase kicker) ─────────────────────────────
export const Eyebrow = ({ children, tone = 'orange', className = '' }) => (
  <span
    className={clsx(
      'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]',
      tone === 'orange' && 'border-brand-gold/30 bg-brand-gold/10 text-brand-gold',
      tone === 'green'  && 'border-brand-emerald/30  bg-brand-emerald/10  text-brand-emerald',
      tone === 'neutral'&& 'border-white/15 bg-white/5 text-zinc-300',
      className,
    )}
  >
    <span className={clsx('h-1.5 w-1.5 rounded-full',
      tone === 'orange' && 'bg-brand-gold',
      tone === 'green'  && 'bg-brand-emerald',
      tone === 'neutral'&& 'bg-zinc-400',
    )} />
    {children}
  </span>
)

// ─── SectionHeading (H2 + optional subtitle) ──────────────────────
export const SectionHeading = ({ eyebrow, eyebrowTone = 'orange', title, subtitle, align = 'left', className = '' }) => (
  <div className={clsx('flex flex-col gap-4', align === 'center' && 'items-center text-center', className)}>
    {eyebrow && <Eyebrow tone={eyebrowTone}>{eyebrow}</Eyebrow>}
    <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-[1.05]">
      {title}
    </h2>
    {subtitle && (
      <p className="max-w-2xl text-base md:text-lg text-zinc-400 leading-relaxed">{subtitle}</p>
    )}
  </div>
)

// ─── SectionDivider — subtle gradient rule ────────────────────────
export const SectionDivider = ({ className = '' }) => (
  <div className={clsx('h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent', className)} />
)

// ─── Buttons ──────────────────────────────────────────────────────
const baseBtn = 'inline-flex items-center gap-2 rounded-full font-semibold text-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/60'

export const GradientButton = ({ children, href, onClick, className = '', size = 'md', trailingIcon = true, ...rest }) => {
  const sizeCls =
    size === 'lg' ? 'px-6 py-3.5 text-[15px]'
    : size === 'sm' ? 'px-4 py-2 text-[13px]'
    : 'px-5 py-2.5'
  const cls = clsx(
    baseBtn, sizeCls,
    'bg-gradient-to-r from-brand-gold to-[#FFE17A] text-black shadow-[0_10px_40px_-10px_rgba(255,193,7,0.55)] hover:shadow-[0_14px_46px_-8px_rgba(255,193,7,0.75)] hover:-translate-y-[1px]',
    className,
  )
  const inner = (
    <>
      {children}
      {trailingIcon && <ArrowRight className="h-4 w-4" />}
    </>
  )
  if (href) return <Link href={href} className={cls} {...rest}>{inner}</Link>
  return <button onClick={onClick} className={cls} {...rest}>{inner}</button>
}

export const GhostButton = ({ children, href, onClick, className = '', size = 'md', trailingIcon = false, ...rest }) => {
  const sizeCls =
    size === 'lg' ? 'px-6 py-3.5 text-[15px]'
    : size === 'sm' ? 'px-4 py-2 text-[13px]'
    : 'px-5 py-2.5'
  const cls = clsx(
    baseBtn, sizeCls,
    'border border-white/15 bg-white/[0.03] text-white backdrop-blur-sm hover:bg-white/[0.07] hover:border-white/25',
    className,
  )
  const inner = (
    <>
      {children}
      {trailingIcon && <ArrowUpRight className="h-4 w-4" />}
    </>
  )
  if (href) return <Link href={href} className={cls} {...rest}>{inner}</Link>
  return <button onClick={onClick} className={cls} {...rest}>{inner}</button>
}

// ─── GlassCard ────────────────────────────────────────────────────
export const GlassCard = ({ children, className = '', interactive = false, ...rest }) => (
  <div
    {...rest}
    className={clsx(
      'relative rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl',
      'shadow-[0_1px_0_0_rgba(255,255,255,0.05)_inset,0_20px_60px_-30px_rgba(0,0,0,0.9)]',
      interactive && 'transition-all duration-300 hover:border-brand-gold/30 hover:bg-white/[0.04] hover:-translate-y-[2px]',
      className,
    )}
  >
    {children}
  </div>
)

// ─── FeatureCard ──────────────────────────────────────────────────
export const FeatureCard = ({ icon: Icon, title, body, tone = 'orange', className = '' }) => (
  <GlassCard interactive className={clsx('p-6 flex flex-col gap-4', className)}>
    {Icon && (
      <div className={clsx(
        'flex h-11 w-11 items-center justify-center rounded-xl border',
        tone === 'orange' && 'border-brand-gold/25 bg-brand-gold/10 text-brand-gold',
        tone === 'green'  && 'border-brand-emerald/25  bg-brand-emerald/10  text-brand-emerald',
        tone === 'neutral'&& 'border-white/15 bg-white/5 text-white',
      )}>
        <Icon className="h-5 w-5" />
      </div>
    )}
    <div className="flex flex-col gap-2">
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
    </div>
  </GlassCard>
)

// ─── StatCard ─────────────────────────────────────────────────────
export const StatCard = ({ value, label, delta, className = '' }) => (
  <GlassCard className={clsx('p-5 flex flex-col gap-1.5', className)}>
    <div className="flex items-baseline gap-2">
      <div className="text-3xl md:text-4xl font-semibold tracking-tight text-white">{value}</div>
      {delta && (
        <span className="inline-flex items-center gap-1 text-[11px] font-mono text-brand-emerald">
          <TrendingUp className="h-3 w-3" />{delta}
        </span>
      )}
    </div>
    <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
  </GlassCard>
)

// ─── Marquee — horizontal auto-scroll ─────────────────────────────
export const Marquee = ({ children, className = '', speed = 40 }) => (
  <div className={clsx('relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]', className)}>
    <div className="flex gap-12 whitespace-nowrap animate-[brand-marquee_var(--dur,40s)_linear_infinite]" style={{ '--dur': `${speed}s` }}>
      {children}
      {children}
    </div>
  </div>
)

// ─── PageShell — hero + slot for Kalki Ai Cloud sub-pages ───────────────────
export const PageShell = ({ eyebrow, title, subtitle, children, primaryCta, secondaryCta, className = '' }) => (
  <section className={clsx('relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden', className)}>
    <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.35]"
      style={{ background: 'radial-gradient(60% 40% at 50% 0%, rgba(255,193,7,0.14), transparent 70%)' }} />
    <div className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_0%,rgba(0,0,0,0.4)_60%,rgba(0,0,0,1)_100%)]" />
    <Container>
      <div className="flex flex-col items-center text-center gap-6 max-w-3xl mx-auto">
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h1 className="text-4xl md:text-6xl font-semibold tracking-tight text-white leading-[1.02]">
          {title}
        </h1>
        {subtitle && (
          <p className="text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">{subtitle}</p>
        )}
        {(primaryCta || secondaryCta) && (
          <div className="mt-2 flex flex-col sm:flex-row items-center gap-3">
            {primaryCta}
            {secondaryCta}
          </div>
        )}
      </div>
      {children && <div className="mt-16 md:mt-20">{children}</div>}
    </Container>
  </section>
)

// ─── Motion helpers ───────────────────────────────────────────────
// Kept intentionally minimal — no framer-motion dependency in Phase 1.
// A CSS animation on mount fades content in from 20px.
export const FadeInUp = ({ children, delay = 0, className = '' }) => (
  <div
    className={clsx('opacity-0 translate-y-4 animate-[brand-fade-up_0.6s_ease-out_forwards]', className)}
    style={{ animationDelay: `${delay}ms` }}
  >
    {children}
  </div>
)

export const Stagger = ({ children, gap = 80, className = '' }) => {
  const arr = React.Children.toArray(children)
  return (
    <div className={className}>
      {arr.map((c, i) => (
        <FadeInUp key={i} delay={i * gap}>{c}</FadeInUp>
      ))}
    </div>
  )
}
